import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { ProgressSteps } from "../chat/ProgressSteps";
import { ReadinessList } from "./ReadinessList";
import { useRediscoverProject, useCommitRediscover, useReadiness } from "../../hooks/useDiscovery";
import { useChatProgress, newRequestId } from "../../hooks/useChat";
import { errorMessage } from "../../hooks/useServers";

const ROW_ICON = { success: "✅", error: "❌", warning: "🟡", pending: "🟡" };
const kindLabel = (k) => (k === "routeros" ? "MikroTik" : k === "olt" ? "OLT" : "Linux");

function Item({ s, checked, onToggle }) {
  const actionable = s.applicable;
  return (
    <div data-testid={`refresh-item-${s.name}`} className="rounded-lg border border-border p-2.5 text-sm space-y-0.5">
      <div className="flex items-start gap-2">
        {actionable && (
          <input data-testid={`refresh-check-${s.name}`} type="checkbox" className="mt-1" checked={checked} onChange={() => onToggle(s.key)} />
        )}
        <div className="min-w-0 flex-1">
          <p className="font-medium">
            {s.name} <span className="text-xs font-normal text-muted-foreground">{kindLabel(s.kind)}{s.via ? ` · vía ${s.via}` : ""}</span>
          </p>
          <p className="text-xs font-mono text-muted-foreground">{s.username ? `${s.username}@` : ""}{s.host || "sin IP"}:{s.port}</p>
          {s.state === "cambio" && (
            <p className="text-xs text-amber-500">
              Drive trae {s.username && s.username !== s.stored_username ? `otro usuario («${s.username}»; guardado: «${s.stored_username}»)` : "otra contraseña"}
              {s.username !== s.stored_username && s.password_differs ? " y otra contraseña" : ""}. No se cambia solo: márcalo si Drive es lo vigente.
            </p>
          )}
          {s.state === "nuevo" && s.status !== "drive" && (
            <p className="text-xs text-amber-500">❌ Faltan credenciales: {s.need}.{s.example ? ` Pega algo como: ${s.example}` : ""}</p>
          )}
          {(s.notes || []).map((n, i) => (
            <p key={i} className="text-xs text-amber-500">⚠️ {n}</p>
          ))}
        </div>
      </div>
    </div>
  );
}

// «Releer carpeta»: compara lo que dice Drive con lo que ya está conectado y, con tu OK, aplica solo lo que marques.
export function RefreshFolderDialog({ open, onOpenChange, project }) {
  const projectId = project?.id;
  const [step, setStep] = useState("reading"); // reading | review | applying | done | error
  const [found, setFound] = useState(null);
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [requestId, setRequestId] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const started = useRef(false);
  const discover = useRediscoverProject(projectId);
  const commit = useCommitRediscover(projectId);
  const working = step === "reading" || step === "applying";
  const { data: progress } = useChatProgress(requestId, working);
  const { data: readiness } = useReadiness(step === "done" ? projectId : null, { poll: true });

  const begin = async () => {
    const rid = newRequestId();
    setRequestId(rid);
    setStartedAt(Date.now());
    setError("");
    setStep("reading");
    try {
      const data = await discover.mutateAsync({ requestId: rid });
      setFound(data);
      setSelected(data.servers.filter((s) => s.state === "nuevo" && s.applicable).map((s) => s.key)); // los cambios de credenciales vienen SIN marcar
      setStep("review");
    } catch (e) {
      setError(errorMessage(e, "No pude leer la carpeta. Inténtalo de nuevo."));
      setStep("error");
    }
  };

  useEffect(() => {
    if (open && !started.current) {
      started.current = true;
      begin();
    }
    if (!open) {
      started.current = false;
      setStep("reading");
      setFound(null);
      setResult(null);
      setError("");
      setRequestId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (key) => setSelected((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));

  const apply = async () => {
    const rid = newRequestId();
    setRequestId(rid);
    setStartedAt(Date.now());
    setStep("applying");
    try {
      setResult(await commit.mutateAsync({ sessionId: found.session_id, selectedKeys: selected, requestId: rid }));
      setStep("done");
    } catch (e) {
      setError(errorMessage(e, "No pude aplicar los cambios."));
      setStep("review");
    }
  };

  const servers = found?.servers || [];
  const news = servers.filter((s) => s.state === "nuevo");
  const changes = servers.filter((s) => s.state === "cambio");
  const same = servers.filter((s) => s.state === "igual");
  const actionable = servers.filter((s) => s.applicable);
  const missing = news.filter((s) => !s.applicable);

  const descriptions = {
    reading: "Leyendo la carpeta de Drive y comparándola con lo conectado…",
    review: "Esto es lo que cambió desde la última lectura.",
    applying: "Aplicando los cambios…",
    done: "Listo.",
    error: "No se pudo completar.",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="refresh-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Releer carpeta — {project?.name}</DialogTitle>
          <DialogDescription>{descriptions[step]}</DialogDescription>
        </DialogHeader>

        {working && (
          <div data-testid="refresh-working">
            <ProgressSteps steps={progress?.steps} startedAt={startedAt} />
          </div>
        )}

        {step === "review" && found && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <p data-testid="refresh-docs" className="text-xs text-muted-foreground">Leí {found.documents?.length || 0} documentos de «{found.folder}».</p>
            {(found.warnings || []).map((w, i) => (
              <p key={i} className="text-xs text-amber-500">⚠️ {w}</p>
            ))}
            {news.length === 0 && changes.length === 0 && found.gone?.length === 0 && (
              <p data-testid="refresh-uptodate" className="text-sm">✅ Todo está al día: Drive y los servidores conectados coinciden ({same.length} servidor{same.length === 1 ? "" : "es"}).</p>
            )}
            {news.length > 0 && (
              <div data-testid="refresh-new" className="space-y-2">
                <p className="text-xs font-medium">🟢 Servidores nuevos en Drive</p>
                {news.map((s) => <Item key={s.key} s={s} checked={selected.includes(s.key)} onToggle={toggle} />)}
              </div>
            )}
            {changes.length > 0 && (
              <div data-testid="refresh-changes" className="space-y-2">
                <p className="text-xs font-medium">🟡 Credenciales distintas en Drive (sin marcar: puede que Drive esté viejo)</p>
                {changes.map((s) => <Item key={s.key} s={s} checked={selected.includes(s.key)} onToggle={toggle} />)}
              </div>
            )}
            {found.gone?.length > 0 && (
              <div data-testid="refresh-gone" className="text-xs text-muted-foreground space-y-0.5">
                <p className="font-medium">Ya no aparecen en los documentos (no se borra nada solo):</p>
                {found.gone.map((g, i) => <p key={i}>• {g.name} ({g.host}:{g.port})</p>)}
              </div>
            )}
            {same.length > 0 && (news.length > 0 || changes.length > 0) && (
              <p data-testid="refresh-same" className="text-xs text-muted-foreground">✅ Sin cambios: {same.map((s) => s.name).join(", ")}.</p>
            )}
            {(found.databases?.length > 0 || found.services?.length > 0) && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">Informativo</summary>
                <ul className="mt-1 space-y-0.5">
                  {(found.databases || []).map((d, i) => <li key={`db-${i}`}>• Base {d.kind}{d.server ? ` en ${d.server}` : ""}{d.name ? ` «${d.name}»` : ""}</li>)}
                  {(found.services || []).map((s, i) => <li key={`sv-${i}`}>• {s.name}{s.note ? ` — ${s.note}` : ""}</li>)}
                </ul>
              </details>
            )}
            {missing.length > 0 && (
              <p data-testid="refresh-missing" className="text-xs text-muted-foreground">Lo que falta por pegar con el candado 🔒 quedará anotado en el proyecto.</p>
            )}
          </div>
        )}

        {step === "done" && result && (
          <div data-testid="refresh-done" className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <p data-testid="refresh-summary" className="text-sm font-medium">{result.summary}</p>
            {result.rows.map((row, i) => (
              <p key={i} className="text-xs text-muted-foreground">{ROW_ICON[row.status] || "🟡"} <span className="font-medium text-foreground">{row.name}</span> — {row.result}</p>
            ))}
            {result.texts.map((t, i) => <p key={i} className="text-xs text-muted-foreground">{t}</p>)}
            <ReadinessList readiness={readiness} />
          </div>
        )}

        {error && <p data-testid="refresh-error" className="text-sm text-red-500">{error}</p>}

        <DialogFooter>
          {step === "error" && <Button data-testid="refresh-retry" variant="outline" onClick={begin}>Reintentar</Button>}
          {step === "review" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
              <Button data-testid="refresh-apply" onClick={apply} disabled={actionable.length === 0 || selected.length === 0}>
                {actionable.length === 0 ? "Nada que aplicar" : `Aplicar cambios (${selected.length})`}
              </Button>
            </>
          )}
          {step === "done" && <Button data-testid="refresh-close" onClick={() => onOpenChange(false)}>Cerrar</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
